import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { authService, sessionController } from '../services/api'
import { SESSION_EXPIRED_EVENT, SESSION_UPDATED_EVENT } from '../services/sessionRefresh'
import { clearPrivateSessionArtifacts, getUserId, normalizeUser } from '../utils/sessionUser'

const AuthContext = createContext()

// Durée avant expiration pour déclencher un refresh proactif (5 minutes)
const PROACTIVE_REFRESH_MARGIN = 5 * 60 * 1000

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [token, setToken] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileError, setProfileError] = useState(null)
  const refreshTimeoutRef = useRef(null)
  const userIdRef = useRef(null)
  const queryClient = useQueryClient()

  const applyUser = useCallback((data) => {
    const next = normalizeUser(data)
    userIdRef.current = getUserId(next)
    setUser(next)
    if (next && Object.prototype.hasOwnProperty.call(next, 'is_admin')) {
      setProfileError(null)
    }
  }, [])

  const dropPreviousUserCaches = useCallback((previousId) => {
    if (!previousId) return
    queryClient.removeQueries({ queryKey: ['saved-items', previousId] })
    queryClient.removeQueries({ queryKey: ['carts', previousId] })
  }, [queryClient])

  const clearSessionData = useCallback(() => {
    setUser(null)
    setToken(null)
    queryClient.clear()
    clearPrivateSessionArtifacts()
  }, [queryClient])

  // Fonction pour décoder le JWT et obtenir l'expiration
  const getTokenExpiration = useCallback((jwtToken) => {
    try {
      const payload = JSON.parse(atob(jwtToken.split('.')[1]))
      return payload.exp ? payload.exp * 1000 : null // Convertir en millisecondes
    } catch {
      return null
    }
  }, [])

  // Fonction pour rafraîchir le token de manière proactive
  const scheduleProactiveRefresh = useCallback((currentToken) => {
    // Annuler tout refresh précédent
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current)
    }

    const expiration = getTokenExpiration(currentToken)
    if (!expiration) return

    const now = Date.now()
    const timeUntilExpiry = expiration - now
    const refreshTime = timeUntilExpiry - PROACTIVE_REFRESH_MARGIN

    // Si le token expire dans moins de 5 minutes, rafraîchir maintenant
    if (refreshTime <= 0) {
      sessionController.refreshSession({ force: true })
        .then((session) => {
          if (session?.access_token) {
            setToken(session.access_token)
            scheduleProactiveRefresh(session.access_token)
          }
        })
        .catch((err) => {
          console.warn('[Auth] Échec du rafraîchissement proactif:', err)
        })
      return
    }

    refreshTimeoutRef.current = setTimeout(() => {
      sessionController.refreshSession({ force: true })
        .then((session) => {
          if (session?.access_token) {
            setToken(session.access_token)
            scheduleProactiveRefresh(session.access_token)
          }
        })
        .catch((err) => {
          console.warn('[Auth] Échec du rafraîchissement proactif:', err)
        })
    }, refreshTime)
  }, [getTokenExpiration])

  useEffect(() => {
    // Vérifier si un token est stocké
    const storedToken = localStorage.getItem('auth_token')
    if (storedToken) {
      setToken(storedToken)
      
      // Vérifier si le token est valide
      authService.getCurrentUser()
        .then(data => {
          applyUser(data)
          if (localStorage.getItem('refresh_token')) {
            scheduleProactiveRefresh(sessionController.readAccessToken() || storedToken)
          }
        })
        .catch(async (error) => {
          if (error.response?.status === 401) {
            try {
              const session = await sessionController.refreshSession()
              if (session?.access_token) {
                setToken(session.access_token)
                const userData = await authService.getCurrentUser()
                applyUser(userData)
                scheduleProactiveRefresh(session.access_token)
                return
              }
            } catch (refreshError) {
              console.error('[Auth] Échec du rafraîchissement:', refreshError)
            }
            localStorage.removeItem('auth_token')
            localStorage.removeItem('refresh_token')
            localStorage.removeItem('session_persistent')
            clearSessionData()
            return
          }
          const fallbackId = getUserId({ id: (() => {
            try {
              return JSON.parse(atob(storedToken.split('.')[1])).sub
            } catch {
              return null
            }
          })() })
          if (fallbackId) {
            applyUser({ id: fallbackId })
          }
          setProfileError(error.response?.data?.detail || 'Impossible de récupérer le profil')
        })
        .finally(() => setLoading(false))
    } else {
      setLoading(false)
    }

    // Écouter l'événement de token expiré
    const handleTokenExpired = () => {
      clearSessionData()
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
      }
    }
    
    // Écouter les changements de token dans localStorage (multi-onglets)
    const handleStorageChange = (e) => {
      if (e.key === 'auth_token') {
        if (e.newValue) {
          setToken(e.newValue)
          scheduleProactiveRefresh(e.newValue)
          authService.getCurrentUser()
            .then((data) => {
              const previousId = userIdRef.current
              const next = normalizeUser(data)
              const nextId = getUserId(next)
              applyUser(next)
              if (previousId && nextId && previousId !== nextId) {
                dropPreviousUserCaches(previousId)
              }
            })
            .catch(() => {})
        } else {
          clearSessionData()
        }
      }
    }
    
    const refetchProtectedAuthErrors = () => {
      queryClient.invalidateQueries({
        predicate: (query) => (
          query.state.status === 'error'
          && query.state.error?.response?.status === 401
        ),
      })
    }

    const handleSessionUpdated = (event) => {
      const nextToken = event.detail?.access_token
      if (!nextToken) return
      setToken(nextToken)
      scheduleProactiveRefresh(nextToken)
      refetchProtectedAuthErrors()
    }

    window.addEventListener(SESSION_EXPIRED_EVENT, handleTokenExpired)
    window.addEventListener(SESSION_UPDATED_EVENT, handleSessionUpdated)
    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleTokenExpired)
      window.removeEventListener(SESSION_UPDATED_EVENT, handleSessionUpdated)
      window.removeEventListener('storage', handleStorageChange)
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
      }
    }
  }, [scheduleProactiveRefresh, applyUser, clearSessionData, dropPreviousUserCaches, queryClient])

  const login = async (email, password, rememberMe = false) => {
    try {
      const previousId = userIdRef.current
      // Toujours demander le refresh_token pour la session
      const response = await authService.login(email, password, true) // Toujours demander refresh_token
      if (response.token) {
        setToken(response.token)
        localStorage.setItem('auth_token', response.token)
        
        // TOUJOURS stocker le refresh_token pour maintenir la session active
        // (même sans "Remember me" - la session reste active tant qu'on utilise l'app)
        if (response.refresh_token) {
          localStorage.setItem('refresh_token', response.refresh_token)
        }
        
        // Toujours activer la persistance de session pour éviter les déconnexions
        localStorage.setItem('session_persistent', 'true')
        
        // Programmer le refresh proactif
        scheduleProactiveRefresh(response.token)
        
        // "Remember me" contrôle uniquement si on stocke email/mdp pour auto-fill
        if (rememberMe) {
          console.log('[Auth] ✅ Session persistante + identifiants mémorisés')
        } else {
          console.log('[Auth] ✅ Session persistante activée (identifiants non mémorisés)')
        }
        
        applyUser(response.user)
        try {
          const me = await authService.getCurrentUser()
          applyUser({
            ...response.user,
            ...me,
            ...(Object.prototype.hasOwnProperty.call(me || {}, 'is_admin')
              ? { is_admin: Boolean(me.is_admin) }
              : {}),
          })
        } catch (error) {
          setProfileError(error.response?.data?.detail || 'Impossible de récupérer le profil')
        }
        const nextId = getUserId(response.user)
        if (previousId && nextId && previousId !== nextId) {
          dropPreviousUserCaches(previousId)
        }
        return { success: true }
      }
      return { success: false, error: 'Token non reçu' }
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Erreur de connexion' }
    }
  }

  const signup = async (email, password) => {
    try {
      await authService.signup(email, password)
      return { success: true }
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Erreur d\'inscription' }
    }
  }

  const logout = () => {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('session_persistent')
    clearSessionData()
    // Annuler le refresh programmé
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current)
    }
    localStorage.removeItem('remembered_pwd')
  }

  return (
    <AuthContext.Provider value={{ user, token, loading, profileError, login, signup, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}





































